import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../../helpers";
import {
  DATATABLE_CUSTOM_STYLES,
  MESSAGES_DATATABLE_CUSTOM_STYLES,
} from "../../../constants/datatableStyles";
import PageTitle from "../../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../../Components/UI/RowLoader";
import Loader from "../../../Components/Loader";
import ErrorAnimationComponent from "../../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import LightButton from "../../../Components/UI/LightButton";

import CustomPagination from "../../../Components/CustomPagination";
import { getClientMessageList } from "../../../apis";
import { transformKeys } from "../../../_helper";

const MessagesList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginator, setPaginator] = useState({});

  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError(null);
      getClientMessageList(currentPage)
        .then((response) => {
          const handleData = response.data.data?.map((d) => transformKeys(d));
          setData(handleData);
          setPaginator({
            last_page: response.data.last_page,
            current_page: response.data.current_page,
          });
        })
        .catch((error) => {
          setError(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchCourses();
  }, [currentPage]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", currentPage);
    navigate({ search: params.toString() });
  }, [navigate, currentPage]);

  const hiddenColumns = ["id"];

  const customColumns = [
    {
      name: "Details",
      selector: (row) => row.name + " - custom",
      ui: (row) => (
        <div className="tw-flex tw-gap-1">
          <Link to={`${row.id}`}>
            <EyeDetailsIcon width="30px" height="30px" />
          </Link>
        </div>
      ),
    },
  ];
  const MESSAGE_CONDITIONAL_ROW_STYLES = [
    {
      when: (row) => row.un_seen_replies > 0,
      style: {
        backgroundColor: "#e0f2fe",
        color: "white",
        fontWeight: "bold",
      },
    },
  ];

  const columns = useMemo(() => {
    return createColumns(data, customColumns, hiddenColumns, true).map(
      (col) => {
        if (col.name === "Message Category") {
          return {
            ...col,
            sortable: false,
            cell: (row) => (
              <div
                className={`${
                  row.message_category === "Out"
                    ? "tw-text-red-500"
                    : "tw-text-green-500"
                }`}
              >
                {row.message_category}
              </div>
            ),
          };
        }
        return {
          ...col,
          sortable: false,
          width: col.name === "Num" ? "70px" : "1fr",
        };
      }
    );
  }, [data]);

  if (loading) return <Loader />;
  if (error) return <ErrorAnimationComponent message={error} />;

  return (
    <>
      <div className="tw-flex max-md:tw-flex-col justify-content-between tw-items-center">
        <PageTitle title="Messages" />
        <LightButton
          hoverColor={"hover:!tw-text-blueLight"}
          text={"Send New Message"}
          icon={<span className="material-icons">add</span>}
          to={"/send-new-message"}
        />
      </div>
      <div className="bg-white flex flex-col mt-4">
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <DataTable
            columns={columns}
            data={data}
            conditionalRowStyles={MESSAGE_CONDITIONAL_ROW_STYLES}
            customStyles={MESSAGES_DATATABLE_CUSTOM_STYLES}
            progressPending={loading}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={paginator?.last_page}
              currentPage={paginator?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default MessagesList;
